import { NextPage } from 'next';
import { isBot } from 'next/dist/server/web/spec-extension/user-agent';
import PricingPage from '@sy/app-client/src/ssrAdapter/pages/PricingPage';

import MarketingTemplate from '~/components/templates/MarketingTemplate';
import buildMetatadata, { getCanonicalUrl } from '~/utils/buildMetadata';
import { getMarketingNavigation } from '~/utils/getNavigation';
import useIHeartTracking from '@sy/app-client/src/ssrAdapter/components/useIHeartTracking';
import { translate, buildGetServerSideProps } from '../getServerSideProps';
import type { CommonContext, InferProps } from '../getServerSideProps';

const Pricing: NextPage<InferProps<typeof getServerSideProps>> = ({
	featureFlags,
	navigation,
	user,
}) => {
	useIHeartTracking({
		action: 'purchase',
	});
	return (
		<MarketingTemplate
			activePage="/pricing"
			featureFlags={featureFlags}
			navigation={navigation}
			overflowVisible
			topPadding={false}
			user={user}
		>
			<PricingPage />
		</MarketingTemplate>
	);
};

const customServerSideProps = async (context: CommonContext) => {
	const { locale, featureFlags, query, requestUrl } = context;
	const { planType } = query;
	const { origin, pathname } = requestUrl;

	// temporary workaround to always show control to the bots for AB tests
	if (isBot(context.req?.headers['user-agent'] || '')) {
		// override any feature flags here
	}

	const isBusiness = planType === 'businesses';

	const canonicalUrl = getCanonicalUrl(origin, pathname, locale);

	return {
		props: {
			metadata: buildMetatadata({
				title: translate(locale, 'common.title.pricing.v2'),
				description: isBusiness
					? translate(locale, 'common.description.pricing.businesses')
					: translate(locale, 'common.description.pricing.individuals'),
				canonical: canonicalUrl,
				host: requestUrl.host,
				pathname,
			}),
			featureFlags,
			navigation: await getMarketingNavigation({ featureFlags, locale, query }),
		},
	};
};

export default Pricing;

export const getServerSideProps = buildGetServerSideProps({
	customServerSideProps,
	// TODO: remove the landing namespace when Black Friday ends
	translationNamespaces: ['common', 'pricing', 'landing', 'paywall'],
	redirect: {
		anonOnly: true,
		redirectTo: '/plan',
		propagateQueryParameters: true,
	},
});
